<template>
  <div>
    <div class="row">
      <div class="col-12 col-sm-12 col-lg-12 col-xl-3">
        <div class="form-group">
          <label class="control-label">Placement</label>
          <multiselect
            :value="placementSelected"
            placeholder
            :options="program.placements"
            track-by="id"
            :allow-empty="false"
            :custom-label="(placement) => `Placement ${placement.number}`"
            :show-labels="false"
            @input="placementWasSelected"
            :searchable="false"
          />
        </div><!-- /.form-group -->
      </div>

      <div class="col-12 col-sm-12 col-lg-12 col-xl-5">
        <div class="form-group">
          <label class="control-label">Agency</label>
          <multiselect
            :value="agencySelected"
            :options="agencies"
            track-by="id"
            placeholder
            label="name"
            :show-labels="false"
            @input="agencyWasSelected"
            :searchable="false"
            :allow-empty="false"
          />
        </div><!-- /.form-group -->
      </div>

      <div class="col-12 col-sm-12 col-lg-12 col-xl-4">
        <div class="form-group">
          <label class="control-label">Graduate Name / GEN</label>
          <input type="text" class="form-control" :value="filters.search" @input="debouncedSearch($event.target.value)">
        </div><!-- /.form-group -->
      </div>
    </div>

    <div class="card">
      <ul class="list-data no-links">
        <li class="data-heading d-none d-xl-block">
          <div class="row">
            <div class="col-12 col-lg-4">
              <p>Graduate</p>
            </div>
            <div class="col-6 col-lg-2">
              <p>GEN</p>
            </div>
            <div class="col-6 col-lg-3">
              <p>Manager</p>
            </div>
          </div><!-- /.row -->
        </li>

        <template v-if="graduates.length > 0 && agencySelected && placementSelected">
          <programs-single-manager-assignments-graduate-list-item
            v-for="graduate in graduates"
            :key="graduate.id"
            :graduate="graduate"
            :placement="placement(graduate)"
            :agency-selected="agencySelected"
          />
        </template>

        <li v-else>
          <div class="row">
            <div class="col-12 text-center">
              <loading-spinner v-if="fetching"/>
              <span v-else>There are no graduates.</span>
            </div>
          </div>
        </li>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

    <lazy-loading :loading="$loading" :reached-bottom="getNextGraduates" ref="lazy">
      <loading-spinner slot="loading"/>
      <span slot="finished"></span>
    </lazy-loading>

    <portal to="modals">
      <create-manager-drawer :agency-default="agencySelected" @created="managerWasCreated"/>
    </portal>
  </div>
</template>

<script>
import { programComputed, programGraduateComputed, programGraduateMethods, agencyMethods, agencyComputed, managerMethods } from 'vx/helpers'
import { helpers } from 'mixins/programs'
import { appliesFilters } from 'mixins'
import { head, debounce } from 'lodash'
import Multiselect from 'vue-multiselect'
import ProgramsSingleManagerAssignmentsGraduateListItem from './ProgramsSingleManagerAssignmentsGraduateListItem'
import CreateManagerDrawer from 'components/CreateManagerDrawer'

export default {
  mixins: [helpers, appliesFilters],

  components: {
    Multiselect,
    ProgramsSingleManagerAssignmentsGraduateListItem,
    CreateManagerDrawer
  },

  data () {
    return {
      filtersDebounced: false,
      filters: {
        search: null,
        placement: null,
        agency: null
      },
      fetching: false,
      placementSelected: null,
      agencySelected: null
    }
  },

  computed: {
    ...programComputed,
    ...programGraduateComputed,
    ...agencyComputed
  },

  async created () {
    await this.getAgencies()

    try {
      this.placementSelected = this.programFindPlacement(parseInt(this.$route.query.placement))
    } catch (error) {
      this.placementSelected = this.programFindPlacement(1)
    }

    this.agencySelected = this.agencies.find(agency => agency.id === parseInt(this.$route.query.agency)) || head(this.agencies)

    this.filters.agency = this.agencySelected ? this.agencySelected.id : null
    this.filters.placement = this.placementSelected ? this.placementSelected.id : null

    await this.getManagers({ params: { agency: this.agencySelected.id } })
  },

  beforeDestroy () {
    this.resetGraduates()
  },

  watch: {
    queryParams: {
      async handler (params) {
        this.fetching = true

        await this.getGraduates({ params })

        this.fetching = false

        this.$nextTick(() => {
          if (this.$refs.lazy) {
            this.$refs.lazy.$emit('reset')
          }
        })
      }
    }
  },

  methods: {
    ...programGraduateMethods,
    ...agencyMethods,
    ...managerMethods,

    debouncedSearch: debounce(function (value) {
      this.filters.search = value
    }, 800),

    placementWasSelected (placement) {
      this.placementSelected = placement

      this.filters.placement = placement ? placement.id : null
    },

    async agencyWasSelected (agency) {
      this.agencySelected = agency

      this.filters.agency = agency ? agency.id : null

      await this.getManagers({ params: { agency: this.filters.agency } })
    },

    placement (graduate) {
      return graduate.placements.find(placement => placement.id === (this.placementSelected ? this.placementSelected.id : null))
    },

    async getNextGraduates () {
      await this.getGraduates({ params: this.queryParams, next: true })
    },

    managerWasCreated (manager) {
      if (manager.agency_id === this.agencySelected.id) {
        this.addManager(manager)
      }
    }
  }
}
</script>
